import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { OtpVerificationMode } from "./otpVerification";

export interface ActivationModeContextValue {
  mode: OtpVerificationMode;
  setMode: (mode: OtpVerificationMode) => void;
  /** Returns the current mode; use this when reading right after setMode + navigate (state may not have updated yet). */
  getMode: () => OtpVerificationMode;
}

export const ActivationModeContext = createContext<
  ActivationModeContextValue | undefined
>(undefined);

export const useActivationModeContext = (): ActivationModeContextValue => {
  const context = useContext(ActivationModeContext);
  if (!context) {
    throw new Error(
      "useActivationModeContext must be used within ActivationModeProvider"
    );
  }
  return context;
};

export const useActivationModeContextOptional = ():
  | ActivationModeContextValue
  | undefined => {
  return useContext(ActivationModeContext);
};

interface ActivationModeProviderProps {
  children: ReactNode;
}

export const ActivationModeProvider: FC<ActivationModeProviderProps> = ({
  children,
}) => {
  const [mode, setModeState] = useState<OtpVerificationMode>("signUp");
  const setMode = useCallback((m: OtpVerificationMode) => {
    setModeState(m);
  }, []);
  const getMode = useCallback(() => mode, [mode]);
  const value = useMemo<ActivationModeContextValue>(
    () => ({ mode, setMode, getMode }),
    [mode, setMode, getMode]
  );
  return (
    <ActivationModeContext.Provider value={value}>
      {children}
    </ActivationModeContext.Provider>
  );
};
